// color-exempt-file — retokenisation Phase 6 ui-themes : dark:bg-[#0a0a0a] preserve le rendu dark historique en l'absence de branding par sondage ; le "poll-card" flottant reste volontairement blanc (bg-white/95 text-gray-900/500/400) quel que soit le theme, lisible sur n'importe quel fond hero (couleur de branding par sondage ou fallback)
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, ExternalLink } from "lucide-react";
import { supabase } from "@core/lib/supabase";
import { SEOHead } from "../components/SEOHead";

interface UserProfile {
  slug: string;
  bio: string | null;
}

interface Template {
  id: string;
  name: string;
  primary_color: string;
  secondary_color: string;
  logo_url: string | null;
}

interface PublishedPoll {
  id: string;
  poll_id: string;
  order: number;
  published_at: string;
}

interface Poll {
  id: string;
  slug: string;
  title: string;
  type: string;
  template: Template | null;
}

export default function PublicProfile() {
  // React Router v6 can't match a param glued to a literal prefix in the
  // same segment (`/@:slug` never matches `/@foo` — verified: matchPath
  // returns null). Route is registered as `/:handle` instead; the `@` is
  // part of the captured value and stripped here.
  const { handle } = useParams<{ handle: string }>();
  const slug = handle?.startsWith("@") ? handle.slice(1) : undefined;
  const { t } = useTranslation(["common", "settings"]);

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [polls, setPolls] = useState<PublishedPoll[]>([]);
  const [pollDetails, setPollDetails] = useState<Record<string, Poll>>({});
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadProfile();
  }, [slug]);

  async function loadProfile() {
    if (!slug) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data: profileData, error: profileError } =
        await supabase.functions.invoke(`public-profile?slug=${slug}`, {
          method: "GET",
        });

      if (profileError) {
        const status = (profileError as { context?: Response }).context?.status;
        if (status === 404) {
          setNotFound(true);
          return;
        }
        throw profileError;
      }

      setProfile(profileData);

      const { data: pollsData, error: pollsError } =
        await supabase.functions.invoke(`public-polls?slug=${slug}`, {
          method: "GET",
        });

      if (!pollsError && Array.isArray(pollsData)) {
        setPolls(pollsData);

        const details: Record<string, Poll> = {};
        for (const pub of pollsData) {
          const { data: pollData, error: pollError } =
            await supabase.functions.invoke(`poll-details?id=${pub.poll_id}`, {
              method: "GET",
            });
          if (!pollError && pollData) {
            details[pub.poll_id] = pollData;
          }
        }
        setPollDetails(details);
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load profile";
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  // Branding : couleurs du premier sondage publié qui a un template
  // (le branding est par sondage, pas par profil — un profil peut publier
  // plusieurs sondages avec des templates différents).
  const activeTemplate = Object.values(pollDetails).find(
    (poll) => poll.template,
  )?.template;
  // Sans template (cas courant), la page suit le thème visuel actif au lieu
  // d'un fond toujours-sombre fixe — le branding par sondage (activeTemplate,
  // hors périmètre) garde la priorité quand il existe.
  const hasTemplate = !!activeTemplate;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-muted-foreground">{t("common:loading")}</p>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-foreground mb-2">404</h1>
          <p className="text-muted-foreground mb-4">
            {t("settings:profileNotFound", { slug })}
          </p>
          <a href="/" className="text-primary hover:text-primary/80 underline">
            {t("common:back_home")}
          </a>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center text-destructive">
          <p className="mb-4">{error}</p>
          <a href="/" className="text-primary hover:text-primary/80 underline">
            {t("common:back_home")}
          </a>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title={profile?.slug || "Public Profile"}
        description={
          profile?.bio || `${profile?.slug}'s public polls on DooDates`
        }
      />

      <div
        className={
          hasTemplate
            ? "min-h-screen"
            : "min-h-screen bg-background dark:bg-[#0a0a0a]"
        }
        style={
          activeTemplate
            ? {
                backgroundColor: activeTemplate.primary_color,
                backgroundImage: `linear-gradient(135deg, ${activeTemplate.primary_color} 0%, ${activeTemplate.secondary_color} 100%)`,
              }
            : undefined
        }
      >
        {/* Header background overlay — uniquement sur le branding par sondage */}
        {hasTemplate && <div className="absolute inset-0 bg-black/40" />}

        <div className="relative z-10 max-w-4xl mx-auto px-4 py-12">
          {/* Profile header */}
          <div className="mb-12">
            {activeTemplate?.logo_url && (
              <div className="mb-6">
                <img
                  src={activeTemplate.logo_url}
                  alt="Logo"
                  className="h-16 w-auto max-w-xs rounded-lg shadow-lg"
                />
              </div>
            )}

            <h1
              className={`text-5xl font-bold mb-2 break-words ${hasTemplate ? "text-white" : "text-foreground"}`}
            >
              @{profile?.slug}
            </h1>

            {profile?.bio && (
              <p
                className={`text-xl max-w-2xl ${hasTemplate ? "text-white/90" : "text-muted-foreground"}`}
              >
                {profile.bio}
              </p>
            )}
          </div>

          {/* Published polls section */}
          {polls.length > 0 ? (
            <div>
              <h2
                className={`text-2xl font-semibold mb-6 ${hasTemplate ? "text-white" : "text-foreground"}`}
              >
                {t("settings:publishedPolls")}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {polls.map((pub) => {
                  const pollData = pollDetails[pub.poll_id];
                  if (!pollData) return null;

                  return (
                    <a
                      key={pub.id}
                      href={`/poll/${pollData.slug}/vote`}
                      className="block p-6 bg-white/95 backdrop-blur rounded-lg shadow-lg hover:shadow-xl transition-shadow group"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-semibold text-gray-900 group-hover:text-primary transition-colors break-words">
                            {pollData.title}
                          </h3>
                          <p className="text-sm text-gray-500 mt-1">
                            {t("settings:pollType")}: {pollData.type}
                          </p>
                        </div>
                        <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-primary flex-shrink-0 mt-0.5" />
                      </div>
                    </a>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="text-center">
              <p
                className={`text-lg ${hasTemplate ? "text-white/80" : "text-muted-foreground"}`}
              >
                {t("settings:noPublishedPolls")}
              </p>
            </div>
          )}

          {/* Footer */}
          <div
            className={`mt-16 pt-8 border-t ${hasTemplate ? "border-white/20" : "border-border"}`}
          >
            <p
              className={`text-sm text-center ${hasTemplate ? "text-white/60" : "text-muted-foreground"}`}
            >
              {t("settings:createdWith")}
              <a
                href="/"
                className={`ml-1 underline ${hasTemplate ? "text-white/80 hover:text-white" : "text-primary hover:text-primary/80"}`}
              >
                DooDates
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
